import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  Validators,
  type AbstractControl,
  type ValidationErrors,
  type ValidatorFn,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  ButtonsContainerComponent,
  IsoDateSelectorComponent,
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { SubscriptionAddOns } from '@principle-theorem/principle-core';
import {
  AddOnOverrideResolution,
  AddOnOverrideState,
  AddOnBillingStatus,
  AddOnKey,
  type IManagementUser,
  type IOrganisationSubscription,
  type ISubscriptionAddOn,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_TIME_FORMAT,
  DATE_WITH_YEAR,
  Firestore,
  getError,
  initMoment,
  isISODateType,
  shareReplayCold,
  toISODate,
  toMoment,
  type ISODateType,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { type Moment } from 'moment-timezone';
import { of, Subject, type Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ManagementService } from '../../auth/management.service';
import { PrPillComponent } from '../../components/pr-pill/pr-pill.component';
import { AddOnOverrideService } from '../add-on-override.service';

const REASON_MIN_LENGTH = 10;
const REASON_REQUIRED_MESSAGE = `Reason is required (min ${REASON_MIN_LENGTH} characters)`;
const FORCE_DISABLED_UNCONFIRMED_MESSAGE =
  'Confirm FORCE DISABLED before saving this override.';
const ADD_ON_LABELS: Record<AddOnKey, string> = {
  [AddOnKey.AiImagingAnalysis]: 'AI Imaging Analysis',
  [AddOnKey.AiReceptionist]: 'AI Receptionist',
  [AddOnKey.AiScribe]: 'AI Scribe',
};
const OVERRIDE_LABELS: Record<AddOnOverrideResolution, string> = {
  [AddOnOverrideResolution.Auto]: 'Auto',
  [AddOnOverrideResolution.ForceEnabled]: 'Force enabled',
  [AddOnOverrideResolution.ForceDisabled]: 'Force disabled',
};
const BILLING_LABELS: Record<AddOnBillingStatus, string> = {
  [AddOnBillingStatus.Active]: 'Active',
  [AddOnBillingStatus.PastDue]: 'Past due',
  [AddOnBillingStatus.Unpaid]: 'Unpaid',
  [AddOnBillingStatus.Canceled]: 'Cancelled',
};

/**
 * Angular's `ValidatorFn` contract requires `null` — not `undefined` — for a
 * control that passes, so the repo-wide `undefined` preference is waived for
 * this single shared value rather than at every return site.
 */
// eslint-disable-next-line no-null/no-null
const NO_VALIDATION_ERRORS: ValidationErrors | null = null;

interface IOverrideFormValue {
  state: AddOnOverrideResolution;
  reason: string;
  expiresAt?: ISODateType;
  forceDisabledConfirmed: boolean;
  clearReason: string;
}

/**
 * Mutable per add-on editing state. Cached across `rows()` recomputes by
 * `sourceSignature` so that a webhook (or another admin) writing to the
 * subscription rebuilds the form, while an operator's in-progress local edit
 * survives any write that did not touch this add-on's override.
 */
interface IAddOnOverrideDraft {
  sourceSignature: string;
  form: TypedFormGroup<IOverrideFormValue>;
  saving: WritableSignal<boolean>;
  error: WritableSignal<string | undefined>;
  clearMode: WritableSignal<boolean>;
  disposed: Subject<void>;
}

/**
 * Fully precomputed view model for a single add-on card.
 *
 * Every value the template renders is resolved here, so the template never
 * invokes a component method per add-on — it reads plain properties and
 * signals off the row instead.
 */
export interface IAddOnOverrideRow {
  addOn: ISubscriptionAddOn;
  key: AddOnKey;
  label: string;
  billingLabel: string;
  overrideLabel: string;
  form: TypedFormGroup<IOverrideFormValue>;
  saving: WritableSignal<boolean>;
  error: WritableSignal<string | undefined>;
  clearMode: WritableSignal<boolean>;
  isEnabled: boolean;
  isExpired: boolean;
  overrideState: AddOnOverrideResolution;
  setByDisplay$: Observable<string | undefined>;
  errorId: string;
}

/**
 * Per add-on override management section.
 *
 * Renders one card per add-on on the subscription, showing the live Stripe status
 * and any tristate `IAddOnOverride` applied on top of it. Management
 * users see an editable tristate dropdown (`Auto` / `ForceEnabled` /
 * `ForceDisabled`) plus a clear-override action; non-management users see a
 * read-only summary of the resolved state.
 *
 * All writes go through `AddOnOverrideService` — this component never
 * touches Firestore directly.
 */
@Component({
  selector: 'pr-add-on-override-section',
  templateUrl: './add-on-override-section.component.html',
  styleUrls: ['./add-on-override-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    IsoDateSelectorComponent,
    ButtonsContainerComponent,
    PrPillComponent,
  ],
})
export class AddOnOverrideSectionComponent {
  private readonly _management = inject(ManagementService);
  private readonly _overrideService = inject(AddOnOverrideService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _drafts = new Map<AddOnKey, IAddOnOverrideDraft>();
  private readonly _displayCache = new Map<
    string,
    Observable<string | undefined>
  >();

  readonly subscription = input.required<WithRef<IOrganisationSubscription>>();

  /** Exposed so the template compares the tristate by enum member, not by magic string. */
  readonly overrideResolution = AddOnOverrideResolution;
  readonly overrideLabels = OVERRIDE_LABELS;

  /** Floor offered by the expiry picker; mirrors the `expiresAt` validator. */
  readonly minExpiryDate: Moment = initMoment();

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly dateFormat = DATE_WITH_YEAR;

  /**
   * Gates the editable form. Non-management users get the read-only summary
   * branch, matching the write-side `ForbiddenError` in `AddOnOverrideService`.
   */
  readonly isManagementUser: Signal<boolean> = toSignal(
    this._management.user$.pipe(map((user) => !!user)),
    { initialValue: false }
  );

  /**
   * One precomputed row per add-on. Recomputes only when the `subscription`
   * input changes; forms and per-row signals are reused from `_drafts`
   * whenever the add-on's override signature is unchanged.
   */
  readonly rows = signal<IAddOnOverrideRow[]>([]);

  constructor() {
    effect(() => {
      const addOns = this.subscription().addOns ?? [];
      const keys = new Set(addOns.map((addOn) => addOn.addOnKey));
      for (const [key, draft] of this._drafts) {
        if (!keys.has(key)) {
          draft.disposed.next();
          draft.disposed.complete();
          this._drafts.delete(key);
        }
      }
      this.rows.set(addOns.map((addOn) => this._buildRow(addOn)));
    });
    this._destroyRef.onDestroy(() => {
      for (const draft of this._drafts.values()) {
        draft.disposed.next();
        draft.disposed.complete();
      }
    });
  }

  /**
   * Persists the row's draft. `Auto` is not a storable state, so selecting it
   * clears the override instead of writing a value.
   */
  async saveOverride(row: IAddOnOverrideRow): Promise<void> {
    const { state, reason, expiresAt, forceDisabledConfirmed } =
      row.form.getRawValue();
    if (state === AddOnOverrideResolution.Auto) {
      if (row.addOn.override) {
        this.enterClearMode(row);
      }
      return;
    }
    if (
      state === AddOnOverrideResolution.ForceDisabled &&
      !forceDisabledConfirmed
    ) {
      row.error.set(FORCE_DISABLED_UNCONFIRMED_MESSAGE);
      return;
    }
    if (row.form.controls.reason.invalid) {
      row.error.set(REASON_REQUIRED_MESSAGE);
      return;
    }
    if (row.form.controls.expiresAt.invalid) {
      return;
    }

    row.saving.set(true);
    row.error.set(undefined);
    try {
      const writeState =
        state === AddOnOverrideResolution.ForceEnabled
          ? AddOnOverrideState.ForceEnabled
          : AddOnOverrideState.ForceDisabled;
      await this._overrideService.setOverride(
        this.subscription().ref,
        row.key,
        writeState,
        {
          reason: reason.trim(),
          expiresAt: expiresAt ? endOfExpiryDay(expiresAt) : undefined,
        }
      );
    } catch (error) {
      row.error.set(getError(error));
    } finally {
      row.saving.set(false);
    }
  }

  enterClearMode(row: IAddOnOverrideRow): void {
    row.clearMode.set(true);
    row.form.controls.clearReason.setValue('');
    row.error.set(undefined);
  }

  cancelClearMode(row: IAddOnOverrideRow): void {
    row.clearMode.set(false);
    row.form.controls.clearReason.setValue('');
    row.error.set(undefined);
  }

  /**
   * Removes the override. The length check is a defensive guard: the Confirm
   * button is already disabled while `clearReason` fails its validator.
   */
  async confirmClearOverride(row: IAddOnOverrideRow): Promise<void> {
    const reason = row.form.getRawValue().clearReason.trim();
    if (reason.length < REASON_MIN_LENGTH) {
      row.error.set(REASON_REQUIRED_MESSAGE);
      return;
    }
    row.saving.set(true);
    row.error.set(undefined);
    try {
      await this._overrideService.clearOverride(
        this.subscription().ref,
        row.key,
        reason
      );
      row.clearMode.set(false);
      row.form.controls.clearReason.setValue('');
    } catch (error) {
      row.error.set(getError(error));
    } finally {
      row.saving.set(false);
    }
  }

  private _buildRow(addOn: ISubscriptionAddOn): IAddOnOverrideRow {
    const draft = this._draft(addOn);
    const now = initMoment();
    const overrideState = SubscriptionAddOns.resolveOverride(addOn, now);
    const expiresAt = addOn.override?.expiresAt;
    return {
      addOn,
      key: addOn.addOnKey,
      label: ADD_ON_LABELS[addOn.addOnKey],
      billingLabel: BILLING_LABELS[addOn.billingStatus],
      overrideLabel: OVERRIDE_LABELS[overrideState],
      form: draft.form,
      saving: draft.saving,
      error: draft.error,
      clearMode: draft.clearMode,
      // The verdict a practice actually experiences: billing, then the
      // override. Must not be replaced with the raw `active` flag — that is
      // billing-only, so a forced add-on would display the opposite of the
      // access it grants.
      isEnabled: SubscriptionAddOns.applyOverride(addOn.active, overrideState),
      isExpired: expiresAt ? toMoment(expiresAt).isBefore(now) : false,
      overrideState,
      setByDisplay$: this._setByDisplay$(addOn),
      errorId: `override-error-${addOn.addOnKey}`,
    };
  }

  private _draft(addOn: ISubscriptionAddOn): IAddOnOverrideDraft {
    const sourceSignature = buildOverrideSourceSignature(addOn);
    const existing = this._drafts.get(addOn.addOnKey);
    if (existing?.sourceSignature === sourceSignature) {
      return existing;
    }
    existing?.disposed.next();
    existing?.disposed.complete();
    const draft = this._createDraft(addOn, sourceSignature);
    this._drafts.set(addOn.addOnKey, draft);
    return draft;
  }

  private _createDraft(
    addOn: ISubscriptionAddOn,
    sourceSignature: string
  ): IAddOnOverrideDraft {
    const saving = signal(false);
    const error = signal<string | undefined>(undefined);
    const clearMode = signal(false);
    const disposed = new Subject<void>();

    const savedState = SubscriptionAddOns.resolveOverride(addOn, initMoment());
    const savedReason = addOn.override?.reason ?? '';
    const state = new TypedFormControl<AddOnOverrideResolution>(savedState);
    const form = new TypedFormGroup<IOverrideFormValue>({
      state,
      reason: new TypedFormControl<string>(
        savedReason,
        requiredReasonWhen(() => state.value !== AddOnOverrideResolution.Auto)
      ),
      expiresAt: new TypedFormControl<ISODateType | undefined>(
        addOn.override?.expiresAt
          ? toISODate(addOn.override.expiresAt)
          : undefined,
        expiryNotInPast
      ),
      forceDisabledConfirmed: new TypedFormControl<boolean>(
        false,
        requiredTrueWhen(
          () => state.value === AddOnOverrideResolution.ForceDisabled
        )
      ),
      clearReason: new TypedFormControl<string>(
        '',
        requiredReasonWhen(() => untracked(clearMode))
      ),
    });

    // A different decision needs its own reason, or the audit would record the
    // previous operator's. `forceDisabledConfirmed` is conditional on `state`,
    // and Angular only re-runs a control's validators when that control changes.
    state.valueChanges.pipe(takeUntil(disposed)).subscribe((selectedState) => {
      form.controls.reason.setValue(
        selectedState === savedState ? savedReason : ''
      );
      form.controls.forceDisabledConfirmed.updateValueAndValidity();
    });
    form.valueChanges
      .pipe(takeUntil(disposed))
      .subscribe(() => error.set(undefined));

    return { sourceSignature, form, saving, error, clearMode, disposed };
  }

  /**
   * Resolves `addOn.override.setBy` to a `name (email)` string by
   * fetching the referenced ManagementUser doc.
   *
   * Emits `undefined` while loading and when the user doc cannot be resolved
   * (deleted user, transient error). Per-ref observables are memoised in
   * `_displayCache` so multiple async-pipe subscriptions and repeated row
   * rebuilds share a single Firestore read.
   */
  private _setByDisplay$(
    addOn: ISubscriptionAddOn
  ): Observable<string | undefined> {
    const setBy = addOn.override?.setBy;
    if (!setBy) {
      return of(undefined);
    }
    const cached = this._displayCache.get(setBy.path);
    if (cached) {
      return cached;
    }
    const display$ = Firestore.safeDoc$<IManagementUser>(setBy).pipe(
      map((user) => (user ? `${user.name} (${user.email})` : undefined)),
      shareReplayCold()
    );
    this._displayCache.set(setBy.path, display$);
    return display$;
  }
}

/**
 * Length of the control's value with surrounding whitespace removed. Matches
 * the `reason.trim()` performed by `AddOnOverrideService` on the write path, so
 * whitespace alone can never satisfy the audit-trail requirement.
 */
function trimmedValueLength(control: AbstractControl): number {
  return typeof control.value === 'string' ? control.value.trim().length : 0;
}

/**
 * Required + min-length measured on the TRIMMED value, applied only while
 * `isRequired()` holds. Used for both the override `reason` (mandatory on the
 * FORCE states) and the `clearReason` (mandatory while the row is in clear
 * mode) so the audit pipeline always captures operator intent.
 */
function requiredReasonWhen(isRequired: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isRequired()) {
      return NO_VALIDATION_ERRORS;
    }
    const length = trimmedValueLength(control);
    if (!length) {
      return { required: true };
    }
    if (length < REASON_MIN_LENGTH) {
      return {
        minlength: {
          requiredLength: REASON_MIN_LENGTH,
          actualLength: length,
        },
      };
    }
    return NO_VALIDATION_ERRORS;
  };
}

/** `requiredTrue`, applied only while `isRequired()` holds. */
function requiredTrueWhen(isRequired: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    isRequired() ? Validators.requiredTrue(control) : NO_VALIDATION_ERRORS;
}

/**
 * Rejects an expiry date earlier than today. `pt-iso-date-selector` writes an
 * ISO date string, so the comparison is by calendar day; picking today is
 * valid because the override lasts until the end of that day.
 */
function expiryNotInPast(control: AbstractControl): ValidationErrors | null {
  if (!isISODateType(control.value)) {
    return NO_VALIDATION_ERRORS;
  }
  const isInPast = moment(control.value).isBefore(initMoment(), 'day');
  return isInPast ? { expiryInPast: true } : NO_VALIDATION_ERRORS;
}

/**
 * An expiry date means "until the end of that day". Persisting the start of
 * the day instead would make an override set for today expire on save.
 */
function endOfExpiryDay(expiryDate: ISODateType): Moment {
  return moment(expiryDate).endOf('day');
}

function buildOverrideSourceSignature(addOn: ISubscriptionAddOn): string {
  const override = addOn.override;
  return JSON.stringify([
    override?.state,
    override?.reason,
    override?.setBy.path,
    override?.setAt.toMillis(),
    override?.expiresAt?.toMillis(),
  ]);
}
